import { Col, Form, Image, Input, Modal, Radio, Row } from "antd";
import React, { useEffect, useState } from "react";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import lang from "../../helper/langHelper";
import SingleImageUpload from "../../components/SingleImageUpload";
import notfound from "../../assets/images/not_found.png";

const FileType = ["image/png", "image/jpg", "image/jpeg", "image/avif", "image/webp", "image/gif"];

const AddForm = ({ api, show, hide, data, refresh }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(notfound);
  const [isImageRemove, setIsImageRemove] = useState(false);

  useEffect(() => {
    if (!data) {
      form.resetFields();
      setImage(null);
      setImagePreview(notfound);
      setIsImageRemove(false);
      return;
    }

    form.setFieldsValue({ ...data });
    setImage(data?.image || null);
    setIsImageRemove(false);
  }, [data, form]);

  useEffect(() => {
    if (!image) {
      setImagePreview(notfound);
      return;
    }

    const selectedFile = image instanceof File ? image : image?.originFileObj;
    if (selectedFile instanceof File) {
      const filePreview = URL.createObjectURL(selectedFile);
      setImagePreview(filePreview);
      return () => URL.revokeObjectURL(filePreview);
    }

    if (typeof image === "string") {
      setImagePreview(image);
      return;
    }

    if (image?.url) {
      setImagePreview(image.url);
      return;
    }

    if (image?.thumbUrl) {
      setImagePreview(image.thumbUrl);
      return;
    }

    setImagePreview(notfound);
  }, [image]);

  const onCreate = (values) => {
    setLoading(true);
    const payload = new FormData();
    payload.append("name", values.name || "");
    payload.append("description", values.description || "");
    payload.append("link", values.link || "");
    payload.append("affiliateLink", values.affiliateLink || "");
    payload.append("sequence", values.sequence || 0);
    payload.append("isActive", values.isActive ?? true);

    const selectedFile = image instanceof File ? image : image?.originFileObj;
    if (selectedFile instanceof File) {
      payload.append("image", selectedFile);
    } else if (typeof image === "string" && image) {
      payload.append("image", image);
    }

    if (data) {
      payload.append("isImageRemove", isImageRemove ? true : false);
    }

    request({
      url: `${data ? api.addEdit + "/" + data._id : api.addEdit}`,
      method: data ? "PUT" : "POST",
      data: payload,
      header: {
        "Content-Type": "multipart/form-data",
      },
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          ShowToast(data.message, Severty.SUCCESS);
          hide();
          refresh();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleImage = (file) => {
    if (!file) {
      setIsImageRemove(Boolean(data));
      setImage(null);
      return;
    }
    setIsImageRemove(false);
    setImage(file || null);
  };

  return (
    <Modal
      open={show}
      width={760}
      okText={data ? lang(`Update`) : lang(`Add`)}
      cancelText={lang(`Cancel`)}
      onCancel={hide}
      okButtonProps={{
        form: "create-vendor",
        htmlType: "submit",
        loading: loading,
      }}
      centered
      className="tab_modal"
    >
      <Form
        id="create-vendor"
        form={form}
        onFinish={onCreate}
        layout="vertical"
        initialValues={{
          isActive: true,
          sequence: 0,
        }}
      >
        <h4 className="modal_title_cls">{data ? lang(`Edit Vendor`) : lang(`Add New Vendor`)}</h4>
        <Row gutter={[16, 0]}>
          <Col span={24} sm={12}>
            <Form.Item
              label={lang(`Vendor Name`)}
              name="name"
              rules={[
                { required: true, message: lang("Vendor name is required") },
                { max: 200, message: lang("Name should not contain more then 200 characters!") },
                { min: 2, message: lang("Name should contain at least 2 characters!") },
              ]}
              normalize={(value) => value.trimStart()}
            >
              <Input autoComplete="off" placeholder={lang(`Enter Vendor Name`)} />
            </Form.Item>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item
              label={lang(`Sequence`)}
              name="sequence"
              rules={[{ required: true, message: lang("Sequence is required") }]}
            >
              <Input autoComplete="off" type="number" placeholder={lang(`Enter Sequence`)} />
            </Form.Item>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item label={lang(`Website Link`)} name="link">
              <Input autoComplete="off" placeholder="https://example.com" />
            </Form.Item>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item
              label={lang(`Affiliate Link`)}
              name="affiliateLink"
              rules={[{ required: true, message: lang("Affiliate link is required") }]}
            >
              <Input autoComplete="off" placeholder="https://affiliate-link.com" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label={lang(`Description`)} name="description">
              <Input.TextArea rows={4} placeholder={lang(`Enter Description`)} />
            </Form.Item>
          </Col>

          <Col span={24} sm={24}>
            <div className="status_wrap">
              <Form.Item label={lang("Active Toggle")} name="isActive">
                <Radio.Group>
                  <Radio value={true}>{lang("Active")}</Radio>
                  <Radio value={false}>{lang("De-Active")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </Col>

          <Col span={24}>
            <Form.Item label={lang("Vendor Logo/Image")} name="image">
              <SingleImageUpload value={image} fileType={FileType} btnName={"Image"} imageType="vendor" onChange={(file) => handleImage(file)} size={5} />
              <p className="img-size-details">Upload a vendor logo or image. Recommended square or wide logo image.</p>
              <div className="mt-2">
                <Image width={120} src={imagePreview} />
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddForm;
